/// <amd-module name="typeahead/core/geolocation/MercuryGeolocationViewController" />
/// <amd-dependency path="typeahead/core/loc" name="loc"/>
import {GeolocationDataProvider, IPosition} from "typeahead/core/geolocation/GeolocationDataProvider";
import {GeolocationClearedMessage} from "typeahead/core/geolocation/message/GeolocationClearedMessage";
import {GeolocationRequestMessage} from "typeahead/core/geolocation/message/GeolocationRequestMessage";
import {GeolocationResponseMessage} from "typeahead/core/geolocation/message/GeolocationResponseMessage";
import {IDataProviderResponse} from "typeahead/core/IDataProvider";
import {ElementFocusedMessage} from "typeahead/core/message/ElementFocusedMessage";
import {FormSubmittedMessage} from "typeahead/core/message/FormSubmittedMessage";
import {SubmitClickedMessage} from "typeahead/core/message/SubmitClickedMessage";
import {SuggestionAutoSelectedMessage} from "typeahead/core/message/SuggestionAutoSelectedMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import * as SearchParametersUpdater from "typeahead/core/SearchParametersUpdater";
import {ISuggestionResult} from "typeahead/core/Suggestion";
import * as SuggestionFactory from "typeahead/core/SuggestionFactory";

export interface IErrorConfig {
    code: number;
    closeTypeahead: boolean;
    message: string;
}

export class MercuryGeolocationViewController {
    private static readonly MESSAGES = {
        ERROR_TITLE: loc.geolocationErrorTitle || "We are unable to determine your current location",
        FINDING_GEOLOCATION: loc.geolocationFindingMessage || "Finding your current location",
        PERMISSION_DENIED: loc.geolocationPermissionDeniedMessage || "Please turn on the location services on your " +
        "device so we can determine your location or type in a destination.",
        POSITION_UNAVAILABLE: loc.geolocationPositionUnavailableMessage || "Please type in a destination.",
        TIMEOUT: loc.geolocationTimeoutMessage || "Please retry or type in a destination.",
        UNKNOWN_ERROR: loc.geolocationUnknownErrorMessage || "Please retry or type in a destination.",
    };

    private domElementId: string;
    private errorConfigMap: {[code: number]: IErrorConfig} = {
        1: {code: 1, closeTypeahead: true, message: MercuryGeolocationViewController.MESSAGES.PERMISSION_DENIED},
        2: {code: 2, closeTypeahead: true, message: MercuryGeolocationViewController.MESSAGES.POSITION_UNAVAILABLE},
        3: {code: 3, closeTypeahead: false, message: MercuryGeolocationViewController.MESSAGES.TIMEOUT},
        4: {code: 4, closeTypeahead: false, message: MercuryGeolocationViewController.MESSAGES.UNKNOWN_ERROR},
    };

    private readonly interstitialContentSelector: string = ".wrap";

    constructor(private geolocationDataProvider: GeolocationDataProvider) {
        TypeaheadMessage.subscribe(ElementFocusedMessage.getTopic(),
            (topic: string, message: ElementFocusedMessage) => {
                this.domElementId = message.getDomElementId();
            });
        TypeaheadMessage.subscribe(SuggestionSelectedMessage.getTopic(),
            (topic: string, message: SuggestionSelectedMessage) => {
                if (GeolocationDataProvider.CATEGORY === message.getSuggestion().getCategory().orElse("") &&
                    !this.getLatLongElementValue()) {
                    this.geolocationDataProvider.findGeolocation(this.getDomElementId());
                }
            });
        TypeaheadMessage.subscribe(SubmitClickedMessage.getTopic(),
            (topic: string, message: SubmitClickedMessage) => {
                if (this.isGeolocationSuggestionSelected(this.getVisibleTypeaheadDivElement())) {
                    if (!this.getLatLongElementValue()) {
                        if (message.getEvent()) {
                            message.getEvent().preventDefault(); // stop submit
                        }
                        this.geolocationDataProvider.findGeolocation(this.getDomElementId(),
                            $(message.getEvent().currentTarget));
                    } else {
                        this.disableLocalStorage();
                    }
                }
            });
        TypeaheadMessage.subscribe(FormSubmittedMessage.getTopic(),
            (topic: string, message: FormSubmittedMessage) => {
                if (message.isFormValid() &&
                        this.isGeolocationSuggestionSelected(this.getVisibleTypeaheadDivElement())) {
                    let $form: JQuery = $("form:has(input[data-provide=utypeahead]):visible");
                    $form.append($("<input>").attr("type", "hidden").attr("name", "sort").val("distance"));
                }
            });

        TypeaheadMessage.subscribe(GeolocationRequestMessage.getTopic(),
            (topic: string, message: GeolocationRequestMessage) => {
                this.showInterstitial();
            });
        TypeaheadMessage.subscribe(GeolocationResponseMessage.getTopic(),
            (topic: string, message: GeolocationResponseMessage) => {
                this.handleGeolocationResponse(message);
            });
        TypeaheadMessage.subscribe(GeolocationClearedMessage.getTopic(),
            (topic: string, message: GeolocationClearedMessage) => {
                this.updateSearchParameters(message.getGeolocationResult(), $("#" + this.getDomElementId()));
            });
    }

    // On Mobile device, show current location, if no previous search or previous search was current location
    public updateView(tab: string): void {
        this.geolocationDataProvider.fetchData(undefined).done((response: IDataProviderResponse) => {
            let suggestionResult: ISuggestionResult = <ISuggestionResult> response.results[0];
            let $inputElement: JQuery = $(`form[data-lob-form=` + tab + `]`).find("input[name=destination]");
            let $autoCompleteDiv: JQuery = $inputElement.closest("div.autocomplete");
            if ($autoCompleteDiv.length) {
                if (!$inputElement.val() || this.isGeolocationCategory($autoCompleteDiv)) {
                    $inputElement.val(suggestionResult.regionNames.lastSearchName);
                    this.updateSearchParameters(suggestionResult, $inputElement);
                    TypeaheadMessage.publish(new SuggestionAutoSelectedMessage(
                        SuggestionFactory.createSuggestion(suggestionResult), $inputElement.attr("id")));
                }
            }
        });
    }

    private handleGeolocationResponse(message: GeolocationResponseMessage) {
        this.hideInterstitial();
        if (message.getPositionError()) {
            this.showErrorModal(message.getPositionError().code);
            $("#" + this.getDomElementId()).val("");
        } else {
            this.updateSearchParameters(message.getGeolocationResult(), $("#" + this.getDomElementId()));
            if (message.getSubmitButton()) {
                message.getSubmitButton().trigger("click");
            }
        }
    }

    private showInterstitial(): void {
        $("#" + this.getDomElementId()).blur();
        uitk.publish("results.changed", {
            contentId: this.interstitialContentSelector,
            disable: true,
            message: "<p>" + MercuryGeolocationViewController.MESSAGES.FINDING_GEOLOCATION + "</p>",
            scrollToTop: false,
            showLoader: true,
        });
    }

    private hideInterstitial(): void {
        uitk.publish("results.complete", {
            ariaLevel: "polite",
            contentId: this.interstitialContentSelector,
            focusElement: this.getDomElementId(),
        });
    }

    private showErrorModal(code: number): void {
        let errorModal = uitk.modal.create({
            content: "<p>" + this.getErrorMessage(code) + "</p>",
            focusClose: this.getDomElementId(),
            modalId: "geolocationErrorModal",
            title: MercuryGeolocationViewController.MESSAGES.ERROR_TITLE,
        });
        uitk.subscribe("modal.close", (topic, modal) => {
            if (modal ===  errorModal) {
                if (!this.errorConfigMap[code].closeTypeahead) {
                    $("#" + this.getDomElementId()).one("focus", () => {
                        let htmlInputEl: HTMLInputElement =
                            <HTMLInputElement> document.getElementById(this.getDomElementId());
                        if (htmlInputEl) {
                            htmlInputEl.setSelectionRange(0, 9999);
                        }
                    });
                }
            }
        });
    }

    private getErrorMessage(code: number) {
        return this.errorConfigMap[code] ? this.errorConfigMap[code].message : this.errorConfigMap[4].message;
    }

    private updateSearchParameters(suggestionResult: ISuggestionResult, $element: JQuery) {
        SearchParametersUpdater.updateForSuggestion(undefined,
            SuggestionFactory.createSuggestion(suggestionResult), $element.data());
    }

    private isGeolocationCategory($typeaheadDivElement: JQuery): boolean {
        return GeolocationDataProvider.CATEGORY === $typeaheadDivElement.find("[id*=category]").val();
    }

    private isGeolocationSuggestionSelected($typeaheadDivElement: JQuery): boolean {
        return GeolocationDataProvider.CATEGORY === $typeaheadDivElement.find("[id*=region_type]").val();
    }

    private getLatLongElementValue(): string {
        return this.getVisibleTypeaheadDivElement().find("[id*=lat_long]").val();
    }

    private getDomElementId(): string {
        return this.domElementId ? this.domElementId :
            $("div.autocomplete:visible").find("input[name=destination]").attr("id");
    }

    private getVisibleTypeaheadDivElement(): JQuery {
        return this.getDomElementId() ? $("#" + this.getDomElementId()).closest("div.autocomplete") :
            $("div.autocomplete:visible");
    }

    private disableLocalStorage(): void {
        this.getVisibleTypeaheadDivElement().find(".gcw-storeable").not("[id*=category]").each((index, element) => {
            $(element).removeClass("gcw-storeable");
        });
    }
}
